<template>
  <card-with-header-and-footer-component :loading="false">
    <template v-slot:header>
      <h2 class="text-lg font-semibold leading-6 text-gray-900">{{ $t('myaccount.about_title') }}</h2>
    </template>

    <dl class="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <div>
        <dt class="text-sm text-gray-600">{{ $t('myaccount.app_name') }}</dt>
        <dd class="mt-1 text-sm font-semibold text-gray-900">BrainsPro</dd>
      </div>
      <div>
        <dt class="text-sm text-gray-600">{{ $t('myaccount.manufacturer') }}</dt>
        <dd class="mt-1 text-sm font-semibold text-gray-900">Brainspro Enseña Online SL</dd>
      </div>
      <div>
        <dt class="text-sm text-gray-600">{{ $t('myaccount.version') }}</dt>
        <dd class="mt-1 text-sm font-semibold text-gray-900 tabular-nums">{{ currentVersion }}</dd>
      </div>
    </dl>

    <div v-if="updates.length" class="mt-8">
      <h3 class="text-sm font-semibold text-gray-900">{{ $t('myaccount.changelog') }}</h3>
      <ul class="mt-3 divide-y divide-gray-100 border-t border-gray-100">
        <li v-for="(update, index) in visibleUpdates" :key="update.uuid" class="grid grid-cols-1 gap-1 py-3 sm:grid-cols-4 sm:gap-4">
          <div class="flex items-center gap-2 sm:flex-col sm:items-start sm:gap-0.5">
            <span class="text-sm font-medium text-gray-900 tabular-nums">{{ update.version }}</span>
            <span class="text-xs text-gray-500 tabular-nums">{{ update.date }}</span>
            <span v-if="index === 0" class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20 sm:mt-1">
              {{ $t('myaccount.current_version') }}
            </span>
          </div>
          <p class="text-sm text-gray-700 sm:col-span-3">{{ update.description }}</p>
        </li>
      </ul>
      <div v-if="updates.length > 5" class="mt-3 flex justify-center">
        <button type="button" @click="showAllUpdates = !showAllUpdates"
                class="rounded-md px-3 py-1.5 text-sm font-medium text-bpblue hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
          {{ showAllUpdates ? $t('myaccount.show_less') : $t('myaccount.show_full_history', { count: updates.length - 5 }) }}
        </button>
      </div>
    </div>

    <template v-slot:footer>
      <p class="text-center text-xs text-gray-500">{{ $t('myaccount.thanks') }}</p>
    </template>
  </card-with-header-and-footer-component>
</template>

<script>
import CardWithHeaderAndFooterComponent from "@/components/panels/CardWithHeaderAndFooterComponent";

export default {
  name: "InformationComponent",
  components: {CardWithHeaderAndFooterComponent},
  data() {
    return {
      updates: [],
      loading: false,
      showAllUpdates: false
    }
  },
  computed: {
    // La versión vigente es la última del historial (antes estaba escrita a mano y desfasada).
    currentVersion() {
      return this.updates[0]?.version ?? ''
    },
    visibleUpdates() {
      return this.showAllUpdates ? this.updates : this.updates.slice(0, 5)
    }
  },
  created() {
    this.updates = [
      {
        uuid: 'bt7vny-h2rq-ck9wm5-381fx',
        version: '2.2.4',
        date: '25/08/2026',
        source: 'Master',
        description: '🆕 Rediseño de la sección de Ventas/Informes: selector de periodo y año, exportación a Excel, e indicador de variación respecto al periodo anterior.',
      },
      {
        uuid: 'qs4dke-w8ny-hj2vr7-604pt',
        version: '2.2.3',
        date: '25/08/2026',
        source: 'Master',
        description: '⚡ Nuevo aviso para funciones bloqueadas por tu plan: ahora se muestra un banner con el contenido real atenuado debajo, en vez de ocultarlo del todo.',
      },
      {
        uuid: 'yn9zfc-l3ea-dp6xt8-927mq',
        version: '2.2.2',
        date: '25/08/2026',
        source: 'Master',
        description: '🐞 El bloque "Dominio corporativo" en Personalización no se estaba mostrando correctamente.',
      },
      {
        uuid: 'hk2wrb-9omd-cs5jn3-418vy',
        version: '2.2.1',
        date: '25/08/2026',
        source: 'Master',
        description: '🐞 El modal de "Nuevo curso" no se veía al abrirlo, solo aparecía el fondo oscurecido.',
      },
      {
        uuid: '72nfkq-al3d-vrx9p4-2938z',
        version: '2.2.0',
        date: '14/08/2026',
        source: 'Master',
        description: '⚡ Rediseño completo de Mi cuenta: menú reorganizado, dashboard de Inicio renovado con seguimiento de progreso, y pestañas de Facturación, Planes y Mi suscripción actualizadas con datos reales. Mejorada la experiencia en formularios y navegación en móvil.',
      },
      {
        uuid: 'q83fnz-p2kd-w7mxa5-119qb',
        version: '2.1.13',
        date: '30/05/2026',
        source: 'Master',
        description: '🆕 El sitio público y el panel del alumno muestran automáticamente el idioma configurado por la academia, con un selector de idiomas nuevo en la cabecera.',
      },
      {
        uuid: 'lk4dso-9fte-h3jvz1-7402r',
        version: '2.1.12',
        date: '21/03/2026',
        source: 'Master',
        description: '🐞 Corregido un bug por el que, con varios roles en una misma academia, a veces se mostraban datos de un rol equivocado.',
      },
      {
        uuid: 'r6bhqx-e0pl-mn8kf2-338sd',
        version: '2.1.11',
        date: '26/12/2024',
        source: 'Master',
        description: '⚡ Se sustituyó el chat de soporte: de Intercom a Tidio.',
      },
      {
        uuid: 'wj9tyc-a4gr-p1xmn7-556ke',
        version: '2.1.10',
        date: '14/06/2024',
        source: 'Master',
        description: '🆕 Al consultar un curso, ahora se puede ver a qué grupo de alumnos está asociado.',
      },
      {
        uuid: 'dz3nfq-l8hb-t5rjc2-914mp',
        version: '2.1.9',
        date: '13/06/2024',
        source: 'Master',
        description: '🐞 Corrección de un fallo visual: el menú de navegación quedaba tapado por otros elementos.',
      },
      {
        uuid: 'kp7wla-6dxs-c9vmq4-227nh',
        version: '2.1.8',
        date: '28/05/2024',
        source: 'Master',
        description: '🆕 Nuevos indicadores de estado (completado / en progreso) en el listado de alumnos inscritos, con una corrección posterior de a quién se le mostraban.',
      },
      {
        uuid: 'nx2fjr-i0be-y6ktp8-673dl',
        version: '2.1.7',
        date: '27/05/2024',
        source: 'Master',
        description: '🆕 Los administradores ya pueden consultar el listado de cursos en los que está inscrito un alumno concreto.',
      },
      {
        uuid: 'ftq8vm-3zac-d4hxn1-850rk',
        version: '2.1.6',
        date: '23/05/2024',
        source: 'Master',
        description: '🆕 Lanzamiento de la función de Grupos: las academias ya pueden crear, editar y eliminar grupos de alumnos, asociarles o quitarles cursos y usuarios, con matriculación automática de los alumnos del grupo en esos cursos y notificaciones al hacer cambios.',
      },
      {
        uuid: 'vhc5rz-o9wj-k2mfy6-045tq',
        version: '2.1.5',
        date: '22/05/2024',
        source: 'Master',
        description: '⚡ La sección "Usuarios" pasó a llamarse "Comunidad", con el menú móvil actualizado a juego.',
      },
      {
        uuid: 'gm1xbn-5spf-w8dcr3-729lh',
        version: '2.1.4',
        date: '26/04/2024',
        source: 'Master',
        description: '⚡ Rediseño de Proyectos con nuevo menú de navegación y pestañas.',
      },
      {
        uuid: 'ycr9fk-t2mq-n5bxa7-361jp',
        version: '2.1.3',
        date: '08/04/2024',
        source: 'Master',
        description: '🆕 Nueva sección "Sobre nosotros" dentro de Mi cuenta.',
      },
      {
        uuid: 'pjb4wv-e7hs-q0lkr9-486mz',
        version: '2.1.2',
        date: '22/03/2024',
        source: 'Master',
        description: '⚡ Crear una cuenta ahora es más rápido: dejaron de pedirse el teléfono y el DNI como campos obligatorios.',
      },
      {
        uuid: 'sq6nfd-c1xj-m9wpa4-717br',
        version: '2.1.1',
        date: '15/03/2024',
        source: 'Master',
        description: '🐞 Se corrigió una fecha mal etiquetada en los cupones (decía "fecha de fin" pero mostraba la de última actualización).',
      },
      {
        uuid: '45iuyh-dfg4-tyhju6-4566h',
        version: '2.1.0',
        date: '11/03/2024',
        source: 'Master',
        description: '🆕 Inclusión de gestión de grupos de alumnos vía API.',
      },
      {
        uuid: '486b-dasd-tyhjaa1-kn56',
        version: '2.0.1',
        date: '24/02/2024',
        source: 'Master',
        description: '🐞 Corrección de errores en componentes de video y mejora de rendimiento.'
      },
      {
        uuid: '4654hb-g422s-fg665-hkg21',
        version: '2.0.0',
        date: '05/02/2024',
        source: 'Master',
        description: '⚡ Actualización masiva de librería y mejora de rendimiento general.'
      }
    ]

        console.log("El contenido de updates es: ", this.updates)
  }
}
</script>

<style scoped>

</style>